import { useState, type FormEvent } from "react";
import type { FxAccountOverview, FxManuellerKontostandInput } from "../../api/kurssicherung.ts";
import { todayIso } from "../../lib/format.ts";
import { liesDezimaleingabe } from "../../lib/dezimaleingabe.ts";
import { kontoMischkursVerfuegbar } from "../../lib/fxKontostand.ts";
import { fehlertext } from "../../lib/fehlertext.ts";
import { hatRecht } from "../../state/berechtigungen.ts";
import { FxDialog } from "./FxDialog.tsx";
import { bewegungText, differenzCent, mischkurs, usd } from "./fxAnzeige.ts";

export interface KontostandKorrigierenDialogProps {
  konto: FxAccountOverview;
  /** Eindeutiger Anzeigename des Kontos (bei gleichnamigen Konten mit Zusatz); Vorgabe: Kontoname. */
  kontoName?: string;
  rechte: ReadonlySet<string>;
  manuellenKontostandAnlegen: (input: FxManuellerKontostandInput) => Promise<unknown>;
  /** Nach erfolgreichem Speichern: Uebersicht neu laden. */
  onGespeichert: () => Promise<void> | void;
  onSchliessen: () => void;
}

/** Dialog "Kontostand korrigieren": manueller Stand (eigenes Kontoereignis) mit Live-Differenz zum aktuellen Stand. */
export function KontostandKorrigierenDialog({ konto, kontoName = konto.account.name, rechte, manuellenKontostandAnlegen, onGespeichert, onSchliessen }: KontostandKorrigierenDialogProps) {
  const [effectiveOn, setEffectiveOn] = useState(todayIso());
  const [balanceUsd, setBalanceUsd] = useState("");
  const [mixedRate, setMixedRate] = useState("");
  const [fehler, setFehler] = useState("");
  const [speichert, setSpeichert] = useState(false);
  const gesperrt = !hatRecht(rechte, "manage_fx");
  const aktuell = konto.current;
  const rueckdatiert = Boolean(aktuell) && Boolean(effectiveOn) && effectiveOn < aktuell!.effectiveOn;
  const betrag = liesDezimaleingabe(balanceUsd, 2);
  const betragFehler = balanceUsd !== "" && "fehler" in betrag ? betrag.fehler : "";
  const differenz = aktuell && balanceUsd !== "" && "wert" in betrag ? differenzCent(betrag.wert, aktuell.balanceUsd) : null;
  const kannSpeichern = !gesperrt && !speichert && Boolean(effectiveOn) && Boolean(balanceUsd) && !betragFehler && Boolean(mixedRate);

  async function speichern(ereignis: FormEvent<HTMLFormElement>): Promise<void> {
    ereignis.preventDefault();
    if (!kannSpeichern) return;
    setSpeichert(true);
    setFehler("");
    try {
      const stand = liesDezimaleingabe(balanceUsd, 2);
      if ("fehler" in stand) { setFehler(`Kontostand: ${stand.fehler}.`); return; }
      const kurs = liesDezimaleingabe(mixedRate, 10);
      if ("fehler" in kurs) { setFehler(`Mischkurs: ${kurs.fehler}.`); return; }
      await manuellenKontostandAnlegen({ accountId: konto.account.id, effectiveOn, balanceUsd: stand.wert, mixedRate: kurs.wert });
      await onGespeichert();
      onSchliessen();
    } catch (ursache) {
      setFehler(fehlertext(ursache, "Der Kontostand konnte nicht erfasst werden.") ?? "");
    } finally {
      setSpeichert(false);
    }
  }

  return (
    <FxDialog
      titelId="kontostandKorrigierenTitel"
      titel="Kontostand korrigieren"
      untertitel={<>{kontoName} · aktuell {aktuell ? `${usd(aktuell.balanceUsd)}${kontoMischkursVerfuegbar(aktuell.balanceUsd) && aktuell.mixedRate ? ` · Mischkurs ${mischkurs(aktuell.mixedRate)}` : ""}` : "noch kein Stand erfasst"}</>}
      beschaeftigt={speichert}
      onSchliessen={onSchliessen}
      onAbsenden={(ereignis) => void speichern(ereignis)}
      fuss={<><button className="btn-sec" type="button" disabled={speichert} onClick={onSchliessen}>Abbrechen</button><button className="btn-primary" type="submit" disabled={!kannSpeichern} title={gesperrt ? "fehlende Berechtigung" : undefined}>{speichert ? "Speichert …" : "Kontostand speichern"}</button></>}
    >
      <label>Stichtag<input aria-label="Stichtag" type="date" value={effectiveOn} disabled={gesperrt || speichert} onChange={(ereignis) => setEffectiveOn(ereignis.target.value)} /></label>
      <label>Neuer Stand in USD<input aria-label="Kontostand in USD" inputMode="decimal" value={balanceUsd} disabled={gesperrt || speichert} onChange={(ereignis) => setBalanceUsd(ereignis.target.value)} /></label>
      <p className="fx-dialog-differenz" aria-live="polite" data-testid="fx-korrektur-differenz">
        {differenz === null ? <span className="fx-dialog-differenz-leer">Die Differenz zum aktuellen Stand erscheint nach der Eingabe.</span> : <>Differenz zum aktuellen Stand: <strong className={differenz > 0n ? "fx-delta-plus" : differenz < 0n ? "fx-delta-minus" : ""}>{bewegungText(differenz)}</strong></>}
      </p>
      <label>Mischkurs<input aria-label="Mischkurs" inputMode="decimal" value={mixedRate} disabled={gesperrt || speichert} onChange={(ereignis) => setMixedRate(ereignis.target.value)} /></label>
      {rueckdatiert && <p className="fx-overview-hint">Rückdatiert: Dieser Stand wird trotzdem zum aktuellen Kontostand.</p>}
      {betragFehler && <p className="fx-overview-error" role="alert">Kontostand: {betragFehler}.</p>}
      {fehler && <p className="fx-overview-error" role="alert">{fehler}</p>}
    </FxDialog>
  );
}
